import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config";
import { useAuth } from "@/components/AuthProvider";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Two paths land here:
//   1. New direct-Google flow:   /auth/callback?token=<JWT>&next=/dashboard
//   2. Legacy Emergent flow:     /auth/callback#session_id=<id>     (kept for backward compat
//      while old browser tabs still have the previous bundle cached. Will be removed after
//      one full redeploy cycle.)
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser, setAuthToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [fatalError, setFatalError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      // Path A: bare error param from backend — show it persistently so the user can read
      // the exact failure code (token_exchange_failed, state_mismatch, etc.) instead of a
      // flashing toast.
      const err = searchParams.get("error");
      if (err) {
        setFatalError(err);
        return;
      }

      // Path B: new direct-Google flow → backend hands us a JWT to store
      const token = searchParams.get("token");
      if (token) {
        try {
          localStorage.setItem("token", token);
          if (typeof setAuthToken === "function") setAuthToken(token);
          const me = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          updateUser(me.data);
          toast.success(`Welcome, ${me.data.name}!`);
          const next = searchParams.get("next") || "/dashboard";
          navigate(next, { replace: true, state: { user: me.data } });
        } catch (e) {
          console.error("Auth bootstrap failed:", e);
          setFatalError(`auth_me_failed: ${e?.response?.status || "network"}`);
        }
        return;
      }

      // Path C: legacy hash-based session_id (older Emergent flow)
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      if (sessionId) {
        setFatalError("legacy_session_id_flow");
        return;
      }

      setFatalError("missing_token");
    };

    run();
  }, [navigate, updateUser, setAuthToken, searchParams]);

  if (fatalError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="auth-callback-error">
        <div className="max-w-md w-full bg-white/5 border border-violet-500/30 rounded-xl p-6 text-center">
          <h1 className="text-2xl font-bold text-violet-400 mb-2">Sign-in failed</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Something went wrong while completing your Google sign-in. The exact error code is:
          </p>
          <code className="block bg-black/40 text-violet-300 text-sm px-3 py-2 rounded font-mono mb-4 break-all" data-testid="auth-callback-error-code">
            {fatalError}
          </code>
          <p className="text-xs text-muted-foreground mb-6">
            If this keeps happening, copy the code above and contact support@creatara.ai — that helps us diagnose.
          </p>
          <Link
            to="/login"
            className="inline-block bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors"
            data-testid="auth-callback-retry-btn"
          >
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback-loading">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );
};
